import { decodeThreeDsHtml, WompiThreeDsFrame } from "../payments/WompiThreeDsFrame";
import AcceptedCardBrands from "../payments/AcceptedCardBrands";
// LOOP_PAYMENT_CARD_PREMIUM_UX_V3
// LOOP_PAYMENT_CARD_PREMIUM_UX_V2
// LOOP_PAYMENT_CARD_PREMIUM_UX_V1
// LOOP Memberships - Wompi Production standard recurring enrollment card.
// M6.3A-3B: standard CARD source + initial 3DS transaction; financial POSTs remain server-gated.
// Raw card fields stay in this browser component and are sent only to Wompi tokenization.

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  CreditCard,
  ExternalLink,
  Loader2,
  Lock,
  ShieldCheck,
} from "lucide-react";
import {
  callCallableNativeSafe,
  getCallableRuntimeDiagnostics,
} from "../../utils/callableNativeSafe";

const WOMPI_PRODUCTION_API_BASE = "https://api.wompi.pa/v1";

function clean(value) {
  return String(value == null ? "" : value).trim();
}

function money(value, currency = "USD") {
  const amount = Number(value || 0);
  try {
    return new Intl.NumberFormat("es-PA", {
      style: "currency",
      currency: clean(currency || "USD").toUpperCase() || "USD",
      minimumFractionDigits: 2,
    }).format(Number.isFinite(amount) ? amount : 0);
  } catch (_) {
    return `$${(Number.isFinite(amount) ? amount : 0).toFixed(2)}`;
  }
}

function errorMessage(error) {
  const raw = String(error?.code || error?.message || error || "").toLowerCase();
  if (raw.includes("app-check") || raw.includes("app check")) {
    return "No pudimos validar este dispositivo. Reabre Loop e inténtalo nuevamente.";
  }
  if (raw.includes("failed-precondition")) {
    return error?.message || "El checkout Production todavía está bloqueado.";
  }
  if (raw.includes("unavailable") || raw.includes("deadline") || raw.includes("timeout")) {
    return "Wompi tardó en responder. No iniciaremos otro cobro automáticamente.";
  }
  return error?.message || "No pudimos continuar con el pago.";
}

function isRetryableNetworkError(error) {
  const raw = String(
    [
      error?.code,
      error?.message,
      error?.details?.reason,
      error?.customData?.originalCode,
      error?.customData?.originalMessage,
      error || "",
    ]
      .filter(Boolean)
      .join(" "),
  ).toLowerCase();

  return Boolean(
    error?.details?.retryable === true ||
    raw.includes("unavailable") ||
    raw.includes("deadline") ||
    raw.includes("timeout") ||
    raw.includes("network") ||
    raw.includes("load failed") ||
    raw.includes("offline") ||
    raw.includes("connection")
  );
}

function stageLabel(stage) {
  const value = clean(stage).toLowerCase();
  if (value === "payment_setup") return "Preparando pago";
  if (value === "tokenization_key") return "Cifrado de tarjeta";
  if (value === "tokenization") return "Tokenización de tarjeta";
  if (value.includes("payment_source")) return "Guardando método de pago";
  if (value === "three_ds_browser_info") return "Verificación del navegador";
  if (value === "three_ds_fingerprint") return "Verificación del dispositivo";
  if (value === "three_ds_challenge") return "Verificación del banco";
  if (value.includes("initial_auth")) return "Autorización del banco";
  if (value.includes("activation")) return "Activando membresía";
  return "Procesando pago";
}

function localSupportCode(subscriptionId, stage, reason) {
  const text = [
    "loop-payment-support-client-v1",
    clean(subscriptionId),
    clean(stage),
    clean(reason),
  ].join("|");

  let hash = 2166136261;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  const hex = (hash >>> 0)
    .toString(16)
    .padStart(8, "0")
    .toUpperCase();

  return `LP-${hex.slice(0, 4)}-${hex.slice(4, 8)}`;
}

function diagnosticFromError(error, {
  subscriptionId = "",
  stage = "payment",
  fallbackReason = "payment_client_error",
} = {}) {
  const details =
    error?.details && typeof error.details === "object"
      ? error.details
      : {};
  const reason = clean(
    details.reason ||
      error?.code ||
      fallbackReason,
  );
  const supportCode = clean(details.supportCode) ||
    localSupportCode(subscriptionId, stage, reason);

  return {
    version:
      clean(details.paymentDiagnosticVersion) ||
      "loop-membership-payment-diagnostic-v1",
    supportCode,
    stage: clean(details.stage) || stage,
    reason: reason || fallbackReason,
    retryable:
      details.retryable === true ||
      isRetryableNetworkError(error),
    final: false,
    reconciliationRequired:
      details.reconciliationRequired === true,
    providerStatus:
      clean(details.providerStatus).toUpperCase() || null,
    threeDsStep:
      clean(details.threeDsStep).toUpperCase() || null,
    statusMessage:
      clean(error?.message, 300) || null,
  };
}

function paymentClientContext() {
  try {
    return getCallableRuntimeDiagnostics();
  } catch (_) {
    return {
      platform: "unknown",
      runtime: "unknown",
      appCheckProvider: null,
      appCheckReady: false,
      online:
        typeof navigator === "undefined"
          ? true
          : navigator.onLine !== false,
      visibility:
        typeof document === "undefined"
          ? null
          : document.visibilityState || null,
    };
  }
}

function base64Url(bytes) {
  let binary = "";
  const input = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  for (let i = 0; i < input.length; i += 1) {
    binary += String.fromCharCode(input[i]);
  }
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");
}

function pemToArrayBuffer(pem) {
  const normalized = clean(pem)
    .replace(/-----BEGIN PUBLIC KEY-----/g, "")
    .replace(/-----END PUBLIC KEY-----/g, "")
    .replace(/\s+/g, "");
  if (!normalized) throw new Error("Wompi no devolvió una llave de tokenización válida.");
  const binary = atob(normalized);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

async function wompiTokenizationPublicKey(setup, subscriptionId, callPayment = callCallableNativeSafe) {
  assertProductionSetup(setup);

  const result = await callPayment({
    functionName: "getMembershipWompiProductionEncryptionKey",
    data: {
      subscriptionId,
    },
    requireAuth: true,
    requireAppCheck: true,
    timeoutMs: 30000,
  });

  const payload = result?.data || {};
  const pem = clean(payload?.publicKey);

  if (
    payload?.ok !== true ||
    payload?.algorithm !== "RSA-OAEP-256" ||
    payload?.contentEncryption !== "A256GCM" ||
    !pem.includes("BEGIN PUBLIC KEY")
  ) {
    throw new Error(
      "Wompi no pudo preparar la llave de cifrado Production.",
    );
  }

  return pem;
}

async function encryptCardJwe(card, pem) {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle || !globalThis.crypto?.getRandomValues) {
    throw new Error("Este dispositivo no soporta la tokenización cifrada requerida.");
  }

  const rsaKey = await subtle.importKey(
    "spki",
    pemToArrayBuffer(pem),
    {
      name: "RSA-OAEP",
      hash: "SHA-256",
    },
    false,
    ["encrypt"],
  );

  const cekBytes = globalThis.crypto.getRandomValues(new Uint8Array(32));
  const cek = await subtle.importKey(
    "raw",
    cekBytes,
    { name: "AES-GCM" },
    false,
    ["encrypt"],
  );

  const protectedJson = JSON.stringify({
    alg: "RSA-OAEP-256",
    enc: "A256GCM",
  });
  const protectedPart = base64Url(new TextEncoder().encode(protectedJson));
  const encryptedKey = await subtle.encrypt(
    { name: "RSA-OAEP" },
    rsaKey,
    cekBytes,
  );

  const iv = globalThis.crypto.getRandomValues(new Uint8Array(12));
  const plaintext = new TextEncoder().encode(JSON.stringify(card));
  const encrypted = new Uint8Array(
    await subtle.encrypt(
      {
        name: "AES-GCM",
        iv,
        additionalData: new TextEncoder().encode(protectedPart),
        tagLength: 128,
      },
      cek,
      plaintext,
    ),
  );

  if (encrypted.length <= 16) {
    throw new Error("No pudimos cifrar la tarjeta.");
  }

  const ciphertext = encrypted.slice(0, encrypted.length - 16);
  const tag = encrypted.slice(encrypted.length - 16);

  return [
    protectedPart,
    base64Url(encryptedKey),
    base64Url(iv),
    base64Url(ciphertext),
    base64Url(tag),
  ].join(".");
}

function assertProductionSetup(setup) {
  const publicKey = clean(setup?.publicKey);
  const apiBase = clean(setup?.apiBase);
  const tokenizationKeyUrl = clean(setup?.tokenizationKeyUrl);
  const tokenizationUrl = clean(setup?.tokenizationUrl);

  if (
    !publicKey.startsWith("pub_prod_") ||
    apiBase !== WOMPI_PRODUCTION_API_BASE ||
    tokenizationKeyUrl !== `${WOMPI_PRODUCTION_API_BASE}/tokens/keys/tokenization` ||
    tokenizationUrl !== `${WOMPI_PRODUCTION_API_BASE}/tokens/cards` ||
    setup?.tokenizationMode !== "jwe-rsa-oaep-256-a256gcm"
  ) {
    throw new Error("La configuración recibida no corresponde al checkout Production esperado.");
  }
}

function cardBrandFromNumber(value) {
  const digits = clean(value).replace(/\D/g, "");

  if (/^4/.test(digits)) {
    return "visa";
  }

  const firstTwo = Number(digits.slice(0, 2));
  const firstFour = Number(digits.slice(0, 4));

  if (
    (firstTwo >= 51 && firstTwo <= 55) ||
    (firstFour >= 2221 && firstFour <= 2720)
  ) {
    return "mastercard";
  }

  return "";
}

function formatCardNumber(value) {
  const digits = clean(value)
    .replace(/\D/g, "")
    .slice(0, 19);

  return digits.match(/.{1,4}/g)?.join(" ") || "";
}

function digitsOnly(value, maxLength) {
  return clean(value)
    .replace(/\D/g, "")
    .slice(0, maxLength);
}

function paymentFieldErrors(card) {
  const number = digitsOnly(card.number, 19);
  const monthRaw = digitsOnly(card.expMonth, 2);
  const yearRaw = digitsOnly(card.expYear, 2);
  const cvc = digitsOnly(card.cvc, 4);
  const cardHolder = clean(card.cardHolder).replace(/\s+/g, " ");
  const errors = {};

  if (cardHolder.length < 2) {
    errors.cardHolder = "Escribe el nombre como aparece en la tarjeta.";
  }

  if (number.length < 13 || number.length > 19) {
    errors.number = "Revisa el número de tarjeta.";
  }

  const month = Number(monthRaw);
  if (monthRaw.length !== 2 || !Number.isInteger(month) || month < 1 || month > 12) {
    errors.expMonth = "Usa un mes entre 01 y 12.";
  }

  if (yearRaw.length !== 2) {
    errors.expYear = "Usa los 2 últimos dígitos del año.";
  }

  if (!errors.expMonth && !errors.expYear) {
    const today = new Date();
    const currentYear = today.getFullYear() % 100;
    const currentMonth = today.getMonth() + 1;
    const year = Number(yearRaw);

    if (year < currentYear || (year === currentYear && month < currentMonth)) {
      errors.expYear = "La tarjeta está vencida.";
    }
  }

  if (cvc.length < 3 || cvc.length > 4) {
    errors.cvc = "Usa 3 o 4 dígitos.";
  }

  return errors;
}

function normalizeCard(card) {
  const number = clean(card.number).replace(/\D/g, "");
  const cvc = clean(card.cvc).replace(/\D/g, "");
  const expMonth = clean(card.expMonth).replace(/\D/g, "").padStart(2, "0").slice(-2);
  const expYear = clean(card.expYear).replace(/\D/g, "").slice(-2);
  const cardHolder = clean(card.cardHolder).replace(/\s+/g, " ");

  if (number.length < 13 || number.length > 19) {
    throw new Error("Revisa el número de tarjeta.");
  }
  if (cvc.length < 3 || cvc.length > 4) {
    throw new Error("Revisa el código de seguridad.");
  }
  const month = Number(expMonth);
  if (!Number.isInteger(month) || month < 1 || month > 12) {
    throw new Error("Revisa el mes de expiración.");
  }
  if (expYear.length !== 2) {
    throw new Error("Usa los últimos 2 dígitos del año de expiración.");
  }
  if (cardHolder.length < 2) {
    throw new Error("Escribe el nombre del tarjetahabiente.");
  }

  return {
    number,
    cvc,
    exp_month: expMonth,
    exp_year: expYear,
    card_holder: cardHolder,
  };
}

async function tokenizeProductionCard(
  setup,
  card,
  subscriptionId,
  callPayment = callCallableNativeSafe,
) {
  assertProductionSetup(setup);

  const normalized =
    normalizeCard(card);

  const pem =
    await wompiTokenizationPublicKey(
      setup,
      subscriptionId,
      callPayment,
    );

  const payload =
    await encryptCardJwe(
      normalized,
      pem,
    );

  /*
   * Only the compact JWE crosses from the browser into Loop backend.
   * PAN, CVC and expiry are never sent as callable fields.
   *
   * Wompi provider contract:
   * GET  https://api.wompi.pa/v1/tokens/keys/tokenization
   * POST https://api.wompi.pa/v1/tokens/cards
   */
  const result =
    await callPayment({
      functionName:
        "tokenizeMembershipWompiProductionEncryptedCard",
      data: {
        subscriptionId,
        payload,
      },
      requireAuth: true,
      requireAppCheck: true,
      timeoutMs: 45000,
    });

  const body =
    result?.data || {};

  const token =
    clean(body?.token);

  if (
    body?.ok !== true ||
    !token.startsWith("tok_prod_")
  ) {
    throw new Error(
      "Wompi no pudo tokenizar la tarjeta cifrada.",
    );
  }

  return {
    id: token,
    brand:
      clean(body?.brand),
    lastFour:
      clean(body?.lastFour),
  };
}

function emptyCard() {
  return {
    number: "",
    cvc: "",
    expMonth: "",
    expYear: "",
    cardHolder: "",
  };
}

function standardEnrollmentIdempotencyKey(subscription) {
  const sid = clean(subscription?.id);
  if (!sid) return "";

  const recoveryKey = clean(
    subscription?.checkout?.standardEnrollmentRetryIdempotencyKey,
  );

  if (
    recoveryKey &&
    recoveryKey.startsWith(`standard-enrollment-retry-v1:${sid}:`) &&
    recoveryKey.length <= 180
  ) {
    return recoveryKey;
  }

  return `standard-enrollment-v1:${sid}`;
}

export default function WompiProductionPaymentCard({
  subscription,
  onSubscription,
  onViewMembership,
  callPayment = callCallableNativeSafe,
  renewalTrial = null,
}) {
  const [setup, setSetup] = useState(null);
  const [setupLoading, setSetupLoading] = useState(false);
  const [setupError, setSetupError] = useState("");
  const [tokenizationProbe, setTokenizationProbe] = useState("");
  const [tokenizationProbeBusy, setTokenizationProbeBusy] = useState(false);
  const [widgetProbeStatus, setWidgetProbeStatus] = useState("");
  const [widgetProbeBusy, setWidgetProbeBusy] = useState(false);
  const [widgetTokenReady, setWidgetTokenReady] = useState(false);
  const widgetProbeHostRef = useRef(null);
  const widgetTokenRef = useRef("");
  const [wompiAccepted, setWompiAccepted] = useState(false);
  const [card, setCard] = useState(emptyCard);
  const [touched, setTouched] = useState({});
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [diagnostic, setDiagnostic] = useState(null);
  const [pollNotice, setPollNotice] = useState("");
  const [autoContinueInitialAuth, setAutoContinueInitialAuth] = useState(false);
  const activationRequestedRef = useRef(false);
  const [encryptedTokenizationResult, setEncryptedTokenizationResult] = useState(null);
  const [paymentSource, setPaymentSource] = useState(null);
  const [transaction, setTransaction] = useState(null);
  const [initialAuthenticationReady, setInitialAuthenticationReady] = useState(false);
  const yearInputRef = useRef(null);
  const cvcInputRef = useRef(null);
  const attemptRef = useRef(null);
  const pollTimerRef = useRef(null);
  const pollInFlightRef = useRef(false);
  const pollDelayRef = useRef(3000);
  const financialAttemptStartedRef = useRef(false);
  const recoveryPollTimerRef = useRef(null);
  const recoveryPollAttemptsRef = useRef(0);

  const status = clean(subscription?.status).toLowerCase();
  const priceLabel = money(subscription?.amount, subscription?.currency || "USD");
  const paymentEnvironment = clean(subscription?.paymentEnvironment).toLowerCase();
  const detectedCardBrand = useMemo(
    () => cardBrandFromNumber(card.number),
    [card.number],
  );
  const fieldErrors = useMemo(
    () => paymentFieldErrors(card),
    [card],
  );
  const cardReady = Object.keys(fieldErrors).length === 0;
  const nextBillingLabel = subscription?.nextBillingAt
    ? new Date(subscription.nextBillingAt).toLocaleDateString("es-PA", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "Mensual";
  const enrollmentIdempotencyKey = useMemo(
    () => standardEnrollmentIdempotencyKey(subscription),
    [
      subscription?.id,
      subscription?.checkout?.standardEnrollmentRetryIdempotencyKey,
    ],
  );
  const transactionId = clean(
    transaction?.id ||
      subscription?.payment?.transactionId ||
      subscription?.payment?.providerTransactionId,
  );
  const paymentSourceStatus = clean(
    paymentSource?.status ||
      subscription?.wompiProductionPaymentSourceStatus ||
      subscription?.payment?.paymentSourceStatus,
  ).toUpperCase();
  const sourceReady =
    initialAuthenticationReady || paymentSourceStatus === "AVAILABLE";
  const recoveryRetryKey = clean(
    subscription?.checkout?.standardEnrollmentRetryIdempotencyKey,
  );
  const recoveryRetryPrepared =
    status === "draft" &&
    sourceReady &&
    !transactionId &&
    recoveryRetryKey.startsWith(
      `standard-enrollment-retry-v1:${clean(subscription?.id)}:`,
    );
  const transactionStatus = clean(
    transaction?.status || subscription?.payment?.providerStatus,
  ).toUpperCase();
  const providerPending = status === "pending_payment";
  const resumePrepared = providerPending && sourceReady && !transactionId &&
    subscription?.checkout?.phase === "standard_source_available" &&
    subscription?.payment?.initialAuthProviderPostStarted !== true;
  const approvedPendingActivation = status === "pending_activation";
  const active = status === "active";
  const terminalInitialAuthFailure =
    status === "draft" &&
    paymentEnvironment === "production" &&
    clean(subscription?.paymentFlowVersion) ===
      "wompi-standard-source-initial-3ds-v1" &&
    clean(subscription?.paymentStatus).toLowerCase() === "failed";
  const persistedDiagnostic =
    subscription?.payment?.diagnostic &&
    typeof subscription.payment.diagnostic === "object"
      ? subscription.payment.diagnostic
      : null;
  const displayDiagnostic = diagnostic || persistedDiagnostic;
  const providerFailureMessage = clean(
    subscription?.payment?.statusMessage ||
      displayDiagnostic?.statusMessage ||
      "",
  );
  const sourcePostCodeEnabled = setup?.sourcePostCodeEnabled === true;
  const initialAuthPostCodeEnabled =
    setup?.initialAuthPostCodeEnabled === true;

  const threeDs = transaction?.threeDs || null;
  const threeDsStep = clean(threeDs?.currentStep).toUpperCase();
  const threeDsStatus = clean(threeDs?.currentStepStatus).toUpperCase();
  const threeDsHtml = useMemo(
    () => decodeThreeDsHtml(threeDs?.methodData || ""),
    [threeDs?.methodData],
  );
  const threeDsTransactionOpen =
    ["PENDING", "PROCESSING"].includes(transactionStatus);
  const hiddenThreeDs =
    threeDsTransactionOpen &&
    ["BROWSER_INFO", "FINGERPRINT"].includes(threeDsStep) &&
    threeDsStatus !== "COMPLETED" &&
    Boolean(threeDsHtml);
  const visibleChallenge =
    threeDsTransactionOpen &&
    threeDsStep === "CHALLENGE" &&
    threeDsStatus !== "COMPLETED" &&
    Boolean(threeDsHtml);

  const ensureAttempt = () => {
    if (!enrollmentIdempotencyKey) {
      throw new Error("No pudimos preparar el intento estándar de Wompi.");
    }

    const current = attemptRef.current;
    if (
      current &&
      current.idempotencyKey === enrollmentIdempotencyKey
    ) {
      return current;
    }

    const next = {
      idempotencyKey: enrollmentIdempotencyKey,
      cardToken: "",
    };
    attemptRef.current = next;
    return next;
  };

  const applyProviderResponse = (payload) => {
    const nextSubscription = payload?.subscription || null;
    const nextPaymentSource = payload?.paymentSource || null;
    const nextTransaction = payload?.transaction || null;
    const nextDiagnostic =
      payload?.diagnostic ||
      nextSubscription?.payment?.diagnostic ||
      null;

    if (nextSubscription) onSubscription(nextSubscription);
    if (nextPaymentSource) setPaymentSource(nextPaymentSource);
    if (nextTransaction) setTransaction(nextTransaction);
    if (nextDiagnostic) setDiagnostic(nextDiagnostic);

    const nextSubscriptionStatus = clean(
      nextSubscription?.status,
    ).toLowerCase();
    const nextPaymentStatus = clean(
      nextSubscription?.paymentStatus,
    ).toLowerCase();
    const nextProviderStatus = clean(
      nextSubscription?.payment?.providerStatus ||
        nextTransaction?.status,
    ).toUpperCase();

    if (
      nextSubscriptionStatus === "active" ||
      nextPaymentStatus === "failed" ||
      ["DECLINED", "ERROR", "VOIDED"].includes(nextProviderStatus)
    ) {
      financialAttemptStartedRef.current = false;
    } else if (
      nextSubscription?.checkout?.paymentStarted === true ||
      ["pending_payment", "pending_activation"].includes(
        nextSubscriptionStatus,
      )
    ) {
      financialAttemptStartedRef.current = true;
    }

    if (
      Object.prototype.hasOwnProperty.call(
        payload || {},
        "initialAuthenticationReady",
      )
    ) {
      setInitialAuthenticationReady(
        payload?.initialAuthenticationReady === true,
      );
    }

    return nextSubscription;
  };

  const recordClientError = (failure, stage, fallbackMessage = "") => {
    const nextDiagnostic = diagnosticFromError(failure, {
      subscriptionId: subscription?.id,
      stage,
      fallbackReason: `${stage}_client_error`,
    });

    setDiagnostic(nextDiagnostic);
    setError(
      fallbackMessage ||
        errorMessage(failure),
    );

    try {
      console.warn("LOOP_PAYMENT_CLIENT_DIAGNOSTIC", {
        supportCode: nextDiagnostic.supportCode,
        stage: nextDiagnostic.stage,
        reason: nextDiagnostic.reason,
        retryable: nextDiagnostic.retryable,
        platform: paymentClientContext().platform,
      });
    } catch (_) {}

    return nextDiagnostic;
  };

  const clearClientPaymentError = () => {
    setError("");
    setPollNotice("");
  };

  const loadSetup = async () => {
    if (!subscription?.id || !enrollmentIdempotencyKey) return;
    setSetupLoading(true);
    setSetupError("");
    try {
      const attempt = ensureAttempt();
      const result = await callPayment({
        functionName: "getMembershipWompiProductionStandardEnrollmentSetup",
        data: {
          subscriptionId: subscription.id,
          idempotencyKey: attempt.idempotencyKey,
          clientContext: paymentClientContext(),
        },
        requireAuth: true,
        requireAppCheck: true,
        timeoutMs: 45000,
      });
      const nextSetup = result?.data || null;
      assertProductionSetup(nextSetup);
      setSetup(nextSetup);
    } catch (setupFailure) {
      setSetup(null);
      const nextDiagnostic = diagnosticFromError(setupFailure, {
        subscriptionId: subscription?.id,
        stage: "payment_setup",
        fallbackReason: "payment_setup_failed",
      });
      setDiagnostic(nextDiagnostic);
      setSetupError(
        `${errorMessage(setupFailure)} Código: ${nextDiagnostic.supportCode}`,
      );
    } finally {
      setSetupLoading(false);
    }
  };

  useEffect(() => {
    attemptRef.current = null;
    setPaymentSource(null);
    setTransaction(null);
    setInitialAuthenticationReady(false);
    setEncryptedTokenizationResult(null);
    setWompiAccepted(false);
    setTouched({});
    setError("");
    setDiagnostic(null);
    setPollNotice("");
    setAutoContinueInitialAuth(false);
    activationRequestedRef.current = false;
    pollInFlightRef.current = false;
    pollDelayRef.current = 3000;
    financialAttemptStartedRef.current = false;
    recoveryPollAttemptsRef.current = 0;
    if (recoveryPollTimerRef.current) {
      window.clearTimeout(recoveryPollTimerRef.current);
      recoveryPollTimerRef.current = null;
    }
  }, [subscription?.id]);

  useEffect(() => {
    if (
      subscription?.id &&
      enrollmentIdempotencyKey &&
      !terminalInitialAuthFailure &&
      ["draft", "pending_payment"].includes(status)
    ) {
      loadSetup();
    } else {
      setSetup(null);
      setSetupError("");
    }
    // setup is intentionally keyed to the current standard enrollment identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    subscription?.id,
    enrollmentIdempotencyKey,
    status,
    terminalInitialAuthFailure,
  ]);

  const loadWompiTokenizationWidgetProbe = () => {
    if (!setup || widgetProbeBusy || widgetTokenReady) return;

    const host = widgetProbeHostRef.current;
    if (!host) {
      setWidgetProbeStatus("ERROR WIDGET: contenedor local no disponible.");
      return;
    }

    setWidgetProbeBusy(true);
    setWidgetProbeStatus(
      "WIDGET LISTO: abre Wompi y tokeniza una sola tarjeta. Los POST financieros siguen bloqueados.",
    );

    host.replaceChildren();

    const sinkName = `loop_wompi_token_sink_${Date.now()}`;
    const sink = document.createElement("iframe");
    sink.name = sinkName;
    sink.title = "Loop Wompi tokenization safety sink";
    sink.style.display = "none";
    host.appendChild(sink);

    const form = document.createElement("form");
    form.method = "POST";
    form.action = "/__loop_wompi_tokenization_blocked_sink__";
    form.target = sinkName;
    form.setAttribute("aria-label", "Wompi tokenization widget canary");

    let captured = false;

    const captureTokenOnly = () => {
      if (captured) return true;

      const hiddenInputs = Array.from(
        form.querySelectorAll('input[type="hidden"]'),
      );

      let token = "";
      for (const input of hiddenInputs) {
        const candidate = clean(input?.value, 1000);
        if (candidate.startsWith("tok_prod_")) {
          token = candidate;
          break;
        }
      }

      if (!token) return false;

      captured = true;
      widgetTokenRef.current = token;
      setWidgetTokenReady(true);
      setWidgetProbeBusy(false);
      setWidgetProbeStatus(
        "TOKEN PRODUCTION CAPTURADO EN MEMORIA: contrato Widget confirmado. Los POST financieros siguen bloqueados.",
      );

      return true;
    };

    const observer = new MutationObserver(() => {
      captureTokenOnly();
    });

    observer.observe(form, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["value"],
    });

    form.addEventListener(
      "submit",
      (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (!captureTokenOnly()) {
          setWidgetProbeStatus(
            "SUBMIT INTERCEPTADO SIN tok_prod_: no se enviará nada a Loop ni a un cobro.",
          );
        }
      },
      true,
    );

    /*
     * Some integrations call form.submit() directly, which bypasses the
     * standard submit event. Override only this isolated probe form.
     */
    try {
      form.submit = () => {
        if (!captureTokenOnly()) {
          setWidgetProbeStatus(
            "SUBMIT DIRECTO INTERCEPTADO SIN tok_prod_: operación bloqueada.",
          );
        }
      };
    } catch (_) {
      // The hidden iframe target remains a fail-closed navigation sink.
    }

    const script = document.createElement("script");
    script.src = "https://checkout.wompi.pa/widget.js";
    script.async = true;
    script.dataset.render = "button";
    script.dataset.widgetOperation = "tokenize";
    script.dataset.publicKey = setup.publicKey;

    script.onload = () => {
      setWidgetProbeBusy(false);
      setWidgetProbeStatus(
        "WIDGET TOKENIZE READY: usa únicamente el botón oficial de Wompi.",
      );
    };

    script.onerror = () => {
      setWidgetProbeStatus(
        "ERROR WIDGET SCRIPT: no pudimos cargar checkout.wompi.pa/widget.js.",
      );
      setWidgetProbeBusy(false);
    };

    form.appendChild(script);
    host.appendChild(form);
  };
  const probeTokenizationConnection = async () => {
    if (!subscription?.id || tokenizationProbeBusy) return;

    setTokenizationProbeBusy(true);
    setTokenizationProbe(
      "INICIANDO: probando conexión Wompi desde el servidor de Loop...",
    );
    setError("");

    try {
      const result = await callPayment({
        functionName:
          "probeMembershipWompiProductionTokenizationServer",
        data: {
          subscriptionId: subscription.id,
        },
        requireAuth: true,
        requireAppCheck: true,
        timeoutMs: 30000,
      });

      const payload = result?.data || {};

      if (
        payload?.ok === true &&
        payload?.source === "firebase_server" &&
        payload?.reachable === true &&
        payload?.hasTokenizationPublicKey === true
      ) {
        setTokenizationProbe(
          `OK SERVER: Wompi respondió HTTP ${payload.httpStatus || 200} desde Firebase. El bloqueo anterior es del navegador/CORS.`,
        );
      } else {
        setTokenizationProbe(
          "ERROR SERVER: respuesta inesperada del probe seguro.",
        );
      }
    } catch (probeError) {
      setTokenizationProbe(
        `ERROR SERVER: ${errorMessage(probeError)}`,
      );
    } finally {
      setTokenizationProbeBusy(false);
    }
  };

  const poll = async ({ allowRecovery = false } = {}) => {
    if (
      !subscription?.id ||
      !enrollmentIdempotencyKey ||
      paymentEnvironment !== "production"
    ) {
      return false;
    }

    if (pollInFlightRef.current) {
      return true;
    }

    if (
      typeof navigator !== "undefined" &&
      navigator.onLine === false
    ) {
      pollDelayRef.current = Math.min(
        Math.max(pollDelayRef.current, 5000) * 1.5,
        30000,
      );
      setPollNotice(
        "Sin conexión. Seguiremos verificando el mismo intento cuando vuelva internet.",
      );
      return true;
    }

    pollInFlightRef.current = true;

    try {
      const attempt = ensureAttempt();
      const result = await callPayment({
        functionName: "getMembershipWompiProductionStandardEnrollmentStatus",
        data: {
          subscriptionId: subscription.id,
          idempotencyKey: attempt.idempotencyKey,
          clientContext: paymentClientContext(),
        },
        requireAuth: true,
        requireAppCheck: true,
        timeoutMs: 45000,
      });
      const payload = result?.data || {};
      const nextSubscription = applyProviderResponse(payload);
      const nextStatus = clean(
        nextSubscription?.status || subscription?.status,
      ).toLowerCase();
      const nextSourceStatus = clean(
        payload?.paymentSource?.status ||
          nextSubscription?.wompiProductionPaymentSourceStatus ||
          nextSubscription?.payment?.paymentSourceStatus,
      ).toUpperCase();
      const nextTransactionStatus = clean(
        payload?.transaction?.status ||
          nextSubscription?.payment?.providerStatus,
      ).toUpperCase();
      const hasTransaction = Boolean(
        payload?.transaction ||
          clean(nextSubscription?.payment?.transactionId) ||
          clean(nextSubscription?.payment?.providerTransactionId),
      );

      pollDelayRef.current = 3000;
      setPollNotice("");
      setError("");

      if (payload?.final === true) {
        recoveryPollAttemptsRef.current = 0;
        financialAttemptStartedRef.current = false;
        return false;
      }

      const again = hasTransaction
        ? Boolean(
            nextStatus === "pending_payment" &&
            ["", "PENDING", "PROCESSING"].includes(
              nextTransactionStatus,
            )
          )
        : Boolean(
            nextStatus === "pending_payment" &&
            nextSourceStatus !== "AVAILABLE"
          );

      if (!again) {
        recoveryPollAttemptsRef.current = 0;
      }

      return again;
    } catch (pollError) {
      const nextDiagnostic = diagnosticFromError(pollError, {
        subscriptionId: subscription?.id,
        stage: transactionId
          ? "initial_auth_polling"
          : "payment_source_polling",
        fallbackReason: "payment_status_poll_failed",
      });
      setDiagnostic(nextDiagnostic);

      const safeToRetryRead = Boolean(
        nextDiagnostic.retryable &&
        (
          providerPending ||
          financialAttemptStartedRef.current ||
          allowRecovery
        )
      );

      if (safeToRetryRead) {
        pollDelayRef.current = Math.min(
          Math.max(pollDelayRef.current, 3000) * 1.7,
          30000,
        );
        setPollNotice(
          `La verificación se interrumpió momentáneamente. Reintentaremos sin crear otro cobro. Código ${nextDiagnostic.supportCode}.`,
        );
        return true;
      }

      setError(errorMessage(pollError));
      return false;
    } finally {
      pollInFlightRef.current = false;
    }
  };

  const recoverAfterUncertainFinancialCall = (
    stage,
    failure,
  ) => {
    if (!isRetryableNetworkError(failure)) {
      return false;
    }

    financialAttemptStartedRef.current = true;
    recoveryPollAttemptsRef.current = 0;

    const nextDiagnostic = diagnosticFromError(failure, {
      subscriptionId: subscription?.id,
      stage,
      fallbackReason: `${stage}_response_uncertain`,
    });
    setDiagnostic(nextDiagnostic);
    setPollNotice(
      `La respuesta se cortó, pero no repetiremos el cobro. Estamos consultando el mismo intento. Código ${nextDiagnostic.supportCode}.`,
    );

    const tick = async () => {
      recoveryPollAttemptsRef.current += 1;
      const again = await poll({ allowRecovery: true });

      if (
        again &&
        recoveryPollAttemptsRef.current < 8
      ) {
        recoveryPollTimerRef.current = window.setTimeout(
          tick,
          Math.max(1500, pollDelayRef.current),
        );
      }
    };

    if (recoveryPollTimerRef.current) {
      window.clearTimeout(recoveryPollTimerRef.current);
    }

    recoveryPollTimerRef.current = window.setTimeout(tick, 900);
    return true;
  };

  const shouldAutoPoll =
    paymentEnvironment === "production" &&
    providerPending &&
    (
      !sourceReady ||
      ["PENDING", "PROCESSING"].includes(transactionStatus)
    );

  useEffect(() => {
    if (!shouldAutoPoll) {
      return undefined;
    }

    // Once React has observed a canonical pending state, the normal auto-poll
    // loop owns read-only recovery. Avoid a second recovery timer racing it.
    if (recoveryPollTimerRef.current) {
      window.clearTimeout(recoveryPollTimerRef.current);
      recoveryPollTimerRef.current = null;
    }

    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;
      const again = await poll();
      if (!cancelled && again) {
        pollTimerRef.current = window.setTimeout(
          tick,
          Math.max(1500, pollDelayRef.current),
        );
      }
    };

    tick();

    return () => {
      cancelled = true;
      if (pollTimerRef.current) {
        window.clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    shouldAutoPoll,
    subscription?.id,
    enrollmentIdempotencyKey,
    transactionStatus,
    sourceReady,
  ]);

  useEffect(() => {
    const resumeReadOnlyRecovery = () => {
      const visible =
        typeof document === "undefined" ||
        document.visibilityState === "visible";

      if (!visible) return;

      if (
        shouldAutoPoll ||
        financialAttemptStartedRef.current
      ) {
        poll({ allowRecovery: true });
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        resumeReadOnlyRecovery();
      }
    };

    window.addEventListener("online", resumeReadOnlyRecovery);
    document.addEventListener(
      "visibilitychange",
      onVisibilityChange,
    );

    return () => {
      window.removeEventListener("online", resumeReadOnlyRecovery);
      document.removeEventListener(
        "visibilitychange",
        onVisibilityChange,
      );
    };
    // Read-only recovery only; it never creates a payment source or transaction.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    subscription?.id,
    enrollmentIdempotencyKey,
    shouldAutoPoll,
  ]);

  const runEncryptedTokenizationOnly = async () => {
    if (
      !subscription?.id ||
      !setup ||
      busy ||
      encryptedTokenizationResult
    ) {
      return;
    }

    setBusy("tokenize");
    setError("");

    try {
      const token = await tokenizeProductionCard(
        setup,
        card,
        subscription.id,
        callPayment,
      );

      const attempt = ensureAttempt();
      attempt.cardToken = token.id;
      attemptRef.current = attempt;

      /*
       * Raw card values disappear from React state immediately after
       * Wompi confirms tokenization.
       */
      setCard(emptyCard());

      setEncryptedTokenizationResult({
        brand:
          clean(token.brand),
        lastFour:
          clean(token.lastFour),
      });
    } catch (tokenizationError) {
      const tokenizationReason = clean(
        tokenizationError?.details?.reason ||
          tokenizationError?.message ||
          "",
        220,
      );

      const tokenizationHttpStatus =
        Number(
          tokenizationError?.details?.httpStatus ||
            0,
        ) || 0;

      if (tokenizationHttpStatus > 0) {
        const providerError =
          tokenizationError?.details?.providerError || {};

        const providerMessages =
          providerError?.messages &&
          typeof providerError.messages === "object"
            ? Object.entries(providerError.messages)
                .flatMap(([field, values]) =>
                  (Array.isArray(values) ? values : [values])
                    .filter(Boolean)
                    .map((value) => `${field}: ${value}`),
                )
                .slice(0, 6)
            : [];

        const providerReason =
          clean(providerError?.reason, 240);

        const providerType =
          clean(providerError?.type, 100);

        const diagnostic =
          providerMessages.length
            ? providerMessages.join(" · ")
            : providerReason
              ? providerReason
              : providerType
                ? providerType
                : "sin detalle adicional";

        setError(
          `Wompi rechazó la tokenización (HTTP ${tokenizationHttpStatus}): ${diagnostic}. No se creó fuente ni se hizo cobro.`,
        );
      } else if (
        tokenizationReason.toLowerCase().includes("timeout") ||
        tokenizationReason.toLowerCase().includes("deadline") ||
        tokenizationReason.toLowerCase().includes("abort") ||
        tokenizationReason.toLowerCase().includes("unavailable")
      ) {
        setError(
          "Wompi no respondió a tiempo durante la tokenización. No se creó fuente ni se hizo cobro.",
        );
      } else {
        setError(
          "No pudimos completar la tokenización con Wompi. No se creó fuente ni se hizo cobro.",
        );
      }
    } finally {
      setBusy("");
    }
  };

  const createStandardSource = async () => {
    if (
      !subscription?.id ||
      !setup ||
      !wompiAccepted ||
      !sourcePostCodeEnabled ||
      busy
    ) {
      return;
    }

    setBusy("source");
    clearClientPaymentError();
    financialAttemptStartedRef.current = true;

    try {
      const attempt = ensureAttempt();

      if (!clean(attempt.cardToken).startsWith("tok_prod_")) {
        const widgetToken = clean(widgetTokenRef.current, 1000);

        if (!widgetToken.startsWith("tok_prod_")) {
          throw new Error(
            "Primero tokeniza la tarjeta con Wompi.",
          );
        }

        attempt.cardToken = widgetToken;
        attemptRef.current = attempt;

        // The Wompi token remains in memory only for this controlled attempt.
        // Loop never receives raw PAN, CVC, or expiry from the Widget.
        setCard(emptyCard());
      }

      const result = await callPayment({
        functionName: "createMembershipWompiProductionStandardPaymentSource",
        data: {
          subscriptionId: subscription.id,
          idempotencyKey: attempt.idempotencyKey,
          cardToken: attempt.cardToken,
          wompiAcceptanceAccepted: true,
          clientContext: paymentClientContext(),
        },
        requireAuth: true,
        requireAppCheck: true,
        timeoutMs: 60000,
      });

      const payload = result?.data || {};
      applyProviderResponse(payload);

      if (payload?.ok === true) {
        attempt.cardToken = "";
        attemptRef.current = attempt;
      }
      return payload;
    } catch (sourceError) {
      const nextDiagnostic = recordClientError(
        sourceError,
        "payment_source_creation",
      );
      if (nextDiagnostic.retryable) {
        recoverAfterUncertainFinancialCall(
          "payment_source_creation",
          sourceError,
        );
      }
      return null;
    } finally {
      setBusy("");
    }
  };

  const startInitialAuthentication = async () => {
    if (
      !subscription?.id ||
      !setup ||
      !sourceReady ||
      !wompiAccepted ||
      !initialAuthPostCodeEnabled ||
      busy
    ) {
      return;
    }

    setBusy("initialAuth");
    clearClientPaymentError();
    financialAttemptStartedRef.current = true;

    try {
      const attempt = ensureAttempt();
      const result = await callPayment({
        functionName:
          "startMembershipWompiProductionStandardInitialAuthentication",
        data: {
          subscriptionId: subscription.id,
          idempotencyKey: attempt.idempotencyKey,
          wompiAcceptanceAccepted: true,
          clientContext: paymentClientContext(),
        },
        requireAuth: true,
        requireAppCheck: true,
        timeoutMs: 60000,
      });

      const payload = result?.data || {};
      applyProviderResponse(payload);
      setAutoContinueInitialAuth(false);
      return payload;
    } catch (initialAuthError) {
      const nextDiagnostic = recordClientError(
        initialAuthError,
        "initial_auth_creation",
      );
      if (nextDiagnostic.retryable) {
        recoverAfterUncertainFinancialCall(
          "initial_auth_creation",
          initialAuthError,
        );
      }
      setAutoContinueInitialAuth(false);
      return null;
    } finally {
      setBusy("");
    }
  };

  const activateEntitlement = async () => {
    if (!subscription?.id || busy) return;
    setBusy("activate");
    setError("");
    try {
      const result = await callPayment({
        functionName: "activateMembershipWompiProductionEntitlement",
        data: { subscriptionId: subscription.id },
        requireAuth: true,
        requireAppCheck: true,
        timeoutMs: 60000,
      });
      const payload = result?.data || {};
      if (payload?.subscription) {
        onSubscription(payload.subscription);
      }
      return payload;
    } catch (activationError) {
      const nextDiagnostic = recordClientError(
        activationError,
        "activation",
      );
      setError(
        `${errorMessage(activationError)} Código de soporte: ${nextDiagnostic.supportCode}.`,
      );
      // Do not loop activation automatically on a persistent error. The user
      // can retry the idempotent activation explicitly, and reopening the
      // checkout will also resume from pending_activation.
      return null;
    } finally {
      setBusy("");
    }
  };

  const startPurchase = async () => {
    if (
      !subscription?.id ||
      !setup ||
      !wompiAccepted ||
      !sourcePostCodeEnabled ||
      !initialAuthPostCodeEnabled ||
      busy
    ) {
      return;
    }

    let purchaseStage = "tokenization";

    setBusy("purchase");
    clearClientPaymentError();
    setDiagnostic(null);
    setAutoContinueInitialAuth(true);

    try {
      const token = await tokenizeProductionCard(
        setup,
        card,
        subscription.id,
        callPayment,
      );

      const attempt = ensureAttempt();
      attempt.cardToken = token.id;
      attemptRef.current = attempt;

      setCard(emptyCard());
      setTouched({});
      setEncryptedTokenizationResult({
        brand: clean(token.brand),
        lastFour: clean(token.lastFour),
      });

      purchaseStage = "payment_source_creation";
      financialAttemptStartedRef.current = true;

      const sourceResult = await callPayment({
        functionName: "createMembershipWompiProductionStandardPaymentSource",
        data: {
          subscriptionId: subscription.id,
          idempotencyKey: attempt.idempotencyKey,
          cardToken: attempt.cardToken,
          wompiAcceptanceAccepted: true,
          clientContext: paymentClientContext(),
        },
        requireAuth: true,
        requireAppCheck: true,
        timeoutMs: 60000,
      });

      const sourcePayload = sourceResult?.data || {};
      applyProviderResponse(sourcePayload);
      attempt.cardToken = "";
      attemptRef.current = attempt;

      const ready =
        sourcePayload?.initialAuthenticationReady === true ||
        clean(sourcePayload?.paymentSource?.status).toUpperCase() === "AVAILABLE";

      if (!ready) {
        return;
      }

      purchaseStage = "initial_auth_creation";

      const authResult = await callPayment({
        functionName:
          "startMembershipWompiProductionStandardInitialAuthentication",
        data: {
          subscriptionId: subscription.id,
          idempotencyKey: attempt.idempotencyKey,
          wompiAcceptanceAccepted: true,
          clientContext: paymentClientContext(),
        },
        requireAuth: true,
        requireAppCheck: true,
        timeoutMs: 60000,
      });

      applyProviderResponse(authResult?.data || {});
      setAutoContinueInitialAuth(false);
    } catch (purchaseError) {
      setAutoContinueInitialAuth(false);

      const nextDiagnostic = diagnosticFromError(purchaseError, {
        subscriptionId: subscription?.id,
        stage: purchaseStage,
        fallbackReason: `${purchaseStage}_failed`,
      });
      setDiagnostic(nextDiagnostic);

      if (purchaseStage === "tokenization") {
        const httpStatus = Number(
          purchaseError?.details?.httpStatus || 0,
        );
        const providerError =
          purchaseError?.details?.providerError || {};
        const providerMessages =
          providerError?.messages &&
          typeof providerError.messages === "object"
            ? Object.entries(providerError.messages)
                .flatMap(([field, values]) =>
                  (Array.isArray(values) ? values : [values])
                    .filter(Boolean)
                    .map((value) => `${field}: ${value}`),
                )
                .slice(0, 4)
            : [];
        const providerReason = clean(providerError?.reason);
        const baseMessage =
          httpStatus > 0
            ? `Wompi rechazó la tokenización${providerMessages.length || providerReason ? `: ${providerMessages.join(" · ") || providerReason}` : ""}. No se creó un método de pago ni se hizo un cobro.`
            : "No pudimos completar la tokenización. No se creó un método de pago ni se hizo un cobro.";

        setError(
          `${baseMessage} Código de soporte: ${nextDiagnostic.supportCode}.`,
        );
      } else {
        setError(
          `${errorMessage(purchaseError)} Código de soporte: ${nextDiagnostic.supportCode}.`,
        );

        if (nextDiagnostic.retryable) {
          recoverAfterUncertainFinancialCall(
            purchaseStage,
            purchaseError,
          );
        }
      }
    } finally {
      setBusy("");
    }
  };

  useEffect(() => {
    if (
      !autoContinueInitialAuth ||
      !providerPending ||
      !sourceReady ||
      transactionId ||
      busy
    ) {
      return;
    }

    startInitialAuthentication();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    autoContinueInitialAuth,
    providerPending,
    sourceReady,
    transactionId,
    busy,
  ]);

  useEffect(() => {
    if (
      !approvedPendingActivation ||
      busy ||
      activationRequestedRef.current
    ) {
      return;
    }

    activationRequestedRef.current = true;
    activateEntitlement();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvedPendingActivation, busy, subscription?.id]);

  return (
    <div className="msc-production-card">
      <div className="msc-production-title msc-production-title-clean">
        <div>
          <span className="msc-production-kicker"><ShieldCheck size={13} /> Pago protegido por Wompi</span>
          <strong>Datos de pago</strong>
          <small>Tu tarjeta se cifra antes de salir de este dispositivo. Loop no almacena tu número de tarjeta, CVC ni fecha de expiración.</small>
        </div>
        <AcceptedCardBrands selectedBrand={detectedCardBrand} />
      </div>

      {setupLoading ? (
        <div className="msc-production-state"><Loader2 className="msc-spin" size={17} /> Preparando pago seguro...</div>
      ) : null}

      {setupError ? (
        <div className="msc-production-error">
          <AlertTriangle size={17} />
          <span>{setupError}</span>
          <button type="button" onClick={loadSetup}>Reintentar</button>
        </div>
      ) : null}

      {error ? (
        <div className="msc-production-error"><AlertTriangle size={17} /> {error}</div>
      ) : null}

      {pollNotice ? (
        <div className="msc-production-recovery">
          <Loader2 className="msc-spin" size={16} />
          <span>{pollNotice}</span>
        </div>
      ) : null}

      {displayDiagnostic && (error || pollNotice || terminalInitialAuthFailure || displayDiagnostic.reconciliationRequired) ? (
        <div className="msc-production-diagnostic">
          <div>
            <span>Diagnóstico de pago</span>
            <strong>{stageLabel(displayDiagnostic.stage)}</strong>
          </div>
          <div>
            <span>Código de soporte</span>
            <strong>{displayDiagnostic.supportCode || "LP-SIN-CODIGO"}</strong>
          </div>
          {displayDiagnostic.reason ? (
            <div>
              <span>Motivo técnico</span>
              <strong>{displayDiagnostic.reason}</strong>
            </div>
          ) : null}
          {displayDiagnostic.providerStatus ? (
            <div>
              <span>Estado Wompi</span>
              <strong>{displayDiagnostic.providerStatus}</strong>
            </div>
          ) : null}
          {displayDiagnostic.threeDsStep ? (
            <div>
              <span>3D Secure</span>
              <strong>{displayDiagnostic.threeDsStep}</strong>
            </div>
          ) : null}
        </div>
      ) : null}

      {setup &&
      status === "draft" &&
      !terminalInitialAuthFailure &&
      !recoveryRetryPrepared &&
      busy === "purchase" ? (
        <div
          className="msc-production-state"
          role="status"
          aria-live="polite"
        >
          <Loader2 className="msc-spin" size={18} />
          <div>
            <strong>Procesando tu pago de forma segura</strong>
            <span>
              Estamos verificando tu tarjeta con Wompi. No cierres esta pantalla.
            </span>
          </div>
        </div>
      ) : null}

      {setup &&
      status === "draft" &&
      !terminalInitialAuthFailure &&
      !recoveryRetryPrepared &&
      busy !== "purchase" ? (
        <>
          <div className="msc-production-form-head">
            <div>
              <span>Tarjeta</span>
              <strong>Ingresa los datos de tu tarjeta</strong>
            </div>
            <div className="msc-production-security"><Lock size={13} /> Conexión cifrada</div>
          </div>

          <div className="msc-production-fields">
            <label>
              <span>Nombre en la tarjeta</span>
              <input
                type="text"
                autoComplete="cc-name"
                value={card.cardHolder}
                placeholder="Como aparece en la tarjeta"
                aria-invalid={Boolean(touched.cardHolder && fieldErrors.cardHolder)}
                onBlur={() => setTouched((prev) => ({ ...prev, cardHolder: true }))}
                onChange={(event) => setCard((prev) => ({ ...prev, cardHolder: event.target.value }))}
              />
              {touched.cardHolder && fieldErrors.cardHolder ? (
                <small className="msc-field-error">{fieldErrors.cardHolder}</small>
              ) : null}
            </label>
            <label className="msc-production-wide">
              <span>Número de tarjeta</span>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="cc-number"
                maxLength={23}
                value={card.number}
                placeholder="1234 5678 9012 3456"
                aria-invalid={Boolean(touched.number && fieldErrors.number)}
                onBlur={() => setTouched((prev) => ({ ...prev, number: true }))}
                onChange={(event) =>
                  setCard((prev) => ({
                    ...prev,
                    number: formatCardNumber(event.target.value),
                  }))
                }
              />
              {touched.number && fieldErrors.number ? (
                <small className="msc-field-error">{fieldErrors.number}</small>
              ) : null}
            </label>
            <label>
              <span>Mes</span>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="cc-exp-month"
                maxLength={2}
                placeholder="MM"
                value={card.expMonth}
                aria-invalid={Boolean(touched.expMonth && fieldErrors.expMonth)}
                onBlur={() => {
                  const raw = digitsOnly(card.expMonth, 2);
                  const padded =
                    raw.length === 1 && Number(raw) >= 1 && Number(raw) <= 9
                      ? `0${raw}`
                      : raw;
                  setCard((prev) => ({ ...prev, expMonth: padded }));
                  setTouched((prev) => ({ ...prev, expMonth: true }));
                }}
                onChange={(event) => {
                  const next = digitsOnly(event.target.value, 2);
                  setCard((prev) => ({ ...prev, expMonth: next }));
                  if (next.length === 2) {
                    window.setTimeout(() => yearInputRef.current?.focus(), 0);
                  }
                }}
              />
              {touched.expMonth && fieldErrors.expMonth ? (
                <small className="msc-field-error">{fieldErrors.expMonth}</small>
              ) : null}
            </label>
            <label>
              <span>Año</span>
              <input
                ref={yearInputRef}
                type="text"
                inputMode="numeric"
                autoComplete="cc-exp-year"
                maxLength={2}
                placeholder="AA"
                value={card.expYear}
                aria-invalid={Boolean(touched.expYear && fieldErrors.expYear)}
                onBlur={() => setTouched((prev) => ({ ...prev, expYear: true }))}
                onChange={(event) => {
                  const next = digitsOnly(event.target.value, 2);
                  setCard((prev) => ({ ...prev, expYear: next }));
                  if (next.length === 2) {
                    window.setTimeout(() => cvcInputRef.current?.focus(), 0);
                  }
                }}
              />
              {touched.expYear && fieldErrors.expYear ? (
                <small className="msc-field-error">{fieldErrors.expYear}</small>
              ) : null}
            </label>
            <label>
              <span>CVC</span>
              <input
                ref={cvcInputRef}
                type="password"
                inputMode="numeric"
                autoComplete="cc-csc"
                maxLength={4}
                value={card.cvc}
                placeholder="123"
                aria-invalid={Boolean(touched.cvc && fieldErrors.cvc)}
                onBlur={() => setTouched((prev) => ({ ...prev, cvc: true }))}
                onChange={(event) =>
                  setCard((prev) => ({
                    ...prev,
                    cvc: digitsOnly(event.target.value, 4),
                  }))
                }
              />
              {touched.cvc && fieldErrors.cvc ? (
                <small className="msc-field-error">{fieldErrors.cvc}</small>
              ) : null}
            </label>
          </div>

          <div className="msc-payment-breakdown">
            <div>
              <span>Cobro de hoy</span>
              <strong>{priceLabel}</strong>
            </div>
            <div>
              <span>{renewalTrial ? "Una renovación a partir de 4 horas" : "Renovación mensual"}</span>
              <strong>{priceLabel}</strong>
            </div>
            <div>
              <span>Cancelación</span>
              <strong>{renewalTrial ? "Automática después de la renovación" : "Cuando quieras"}</strong>
            </div>
          </div>

          <label className="msc-production-consent">
            <input
              type="checkbox"
              checked={wompiAccepted}
              onChange={(event) => setWompiAccepted(event.target.checked)}
            />
            <span>
              <strong>Autorizar pago y renovaciones</strong>
              <span className="msc-production-consent-desktop-text">
                {renewalTrial ? "Acepto los términos de Wompi y autorizo USD 4 hoy y una única renovación de USD 4 a partir de cuatro horas después. Total previsto: USD 8. La revisión de vencimientos es cada hora." : "Acepto los términos vigentes de Wompi y autorizo esta tarjeta para el pago de hoy y las renovaciones mensuales de esta membresía."}
              </span>
              <span className="msc-production-consent-mobile-text">
                {renewalTrial ? "Autorizo USD 4 hoy y una renovación de USD 4 a partir de cuatro horas. Total: USD 8." : "Autorizo este pago y las renovaciones mensuales."}
              </span>
              {setup?.acceptance?.permalink ? (
                <a
                  href={setup.acceptance.permalink}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(event) => event.stopPropagation()}
                >
                  Ver términos <ExternalLink size={12} />
                </a>
              ) : null}
            </span>
          </label>

          <button
            type="button"
            className="msc-production-primary"
            onClick={startPurchase}
            disabled={
              !cardReady ||
              !wompiAccepted ||
              !sourcePostCodeEnabled ||
              !initialAuthPostCodeEnabled ||
              Boolean(busy)
            }
          >
            {busy === "purchase"
              ? "Procesando de forma segura..."
              : `Pagar ${priceLabel} y activar membresía`}
          </button>

          <div className="msc-production-foot">
            <Lock size={15} />
            <span>Pago seguro procesado por Wompi. Loop no almacena los datos de tu tarjeta.</span>
          </div>
        </>
      ) : null}

      {setup && (recoveryRetryPrepared || resumePrepared) ? (
        <>
          <div className="msc-production-state">
            <CheckCircle2 size={18} />
            <div>
              <strong>Método de pago listo</strong>
              <span>Reutilizaremos de forma segura el método de pago ya guardado. No necesitas volver a introducir la tarjeta.</span>
            </div>
          </div>

          <label className="msc-production-consent">
            <input
              type="checkbox"
              checked={wompiAccepted}
              onChange={(event) => setWompiAccepted(event.target.checked)}
            />
            <span>
              <strong>{resumePrepared ? "Autorizar y continuar el pago" : "Confirmar nuevo intento de pago"}</strong>
              <span className="msc-production-consent-desktop-text">
                {renewalTrial ? "Acepto los términos de Wompi y autorizo reintentar el pago inicial de USD 4 y una renovación de USD 4 a partir de cuatro horas después de aprobarse. Total previsto: USD 8." : <>Acepto los términos vigentes de Wompi y autorizo un nuevo intento de pago inicial de {priceLabel} usando el método de pago guardado, además de las renovaciones mensuales de esta membresía.</>}
              </span>
              <span className="msc-production-consent-mobile-text">
                {renewalTrial ? "Autorizo reintentar USD 4 y una renovación de USD 4 a partir de cuatro horas." : <>Autorizo este intento de {priceLabel} y las renovaciones mensuales.</>}
              </span>
              {setup?.acceptance?.permalink ? (
                <a
                  href={setup.acceptance.permalink}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(event) => event.stopPropagation()}
                >
                  Ver términos <ExternalLink size={12} />
                </a>
              ) : null}
            </span>
          </label>

          <button
            type="button"
            className="msc-production-primary"
            onClick={startInitialAuthentication}
            disabled={
              !wompiAccepted ||
              !initialAuthPostCodeEnabled ||
              Boolean(busy)
            }
          >
            {busy === "initialAuth"
              ? "Procesando pago..."
              : `Continuar pago de ${priceLabel}`}
          </button>

          <div className="msc-production-foot">
            <Lock size={15} />
            <span>No volveremos a tokenizar la tarjeta ni crear un segundo método de pago.</span>
          </div>
        </>
      ) : null}

      {terminalInitialAuthFailure ? (
        <div className="msc-production-error">
          <AlertTriangle size={18} />
          <div>
            <strong>No pudimos aprobar este pago</strong>
            <span>
              {providerFailureMessage
                ? `${providerFailureMessage} `
                : ""}
              Por seguridad no repetiremos automáticamente el mismo intento financiero.
              {displayDiagnostic?.supportCode
                ? ` Código de soporte: ${displayDiagnostic.supportCode}.`
                : ""}
            </span>
          </div>
        </div>
      ) : null}

      {providerPending && !resumePrepared ? (
        <div className="msc-production-pending">
          <Loader2 className="msc-spin" size={18} />
          <div>
            <strong>{transactionId ? "Confirmando tu pago" : "Preparando tu método de pago"}</strong>
            <span>{transactionId ? "Wompi está procesando la transacción. Si tu banco solicita verificación, complétala abajo." : "Estamos preparando la tarjeta para la renovación automática."}</span>
          </div>
          {!transactionId && sourceReady ? (
            <button
              type="button"
              onClick={startInitialAuthentication}
              disabled={!wompiAccepted || Boolean(busy)}
            >
              Continuar pago
            </button>
          ) : (
            <button type="button" onClick={poll} disabled={Boolean(busy)}>
              Actualizar estado
            </button>
          )}
        </div>
      ) : null}

      {approvedPendingActivation ? (
        <div className="msc-production-approved">
          <Loader2 className="msc-spin" size={20} />
          <div>
            <strong>Pago aprobado</strong>
            <span>Estamos activando tus beneficios. No necesitas hacer nada más.</span>
          </div>
          {error ? (
            <button type="button" onClick={activateEntitlement} disabled={Boolean(busy)}>
              Reintentar activación
            </button>
          ) : null}
        </div>
      ) : null}

      {active ? (
        <div className="msc-membership-complete">
          <div className="msc-membership-complete-icon"><CheckCircle2 size={30} /></div>
          <span className="msc-membership-complete-kicker">Pago confirmado</span>
          <h3>¡Tu membresía está activa!</h3>
          <p>{renewalTrial ? "Pago inicial aprobado. La prueba tiene una renovación de USD 4 a partir de cuatro horas después; al confirmarse, se desactivarán futuros cobros." : "Ya puedes usar tus beneficios. Tu método de pago quedó guardado de forma segura para las próximas renovaciones."}</p>

          <div className="msc-membership-complete-grid">
            <div>
              <span>Estado</span>
              <strong>Activa</strong>
            </div>
            <div>
              <span>Próxima renovación</span>
              <strong>{nextBillingLabel}</strong>
            </div>
          </div>

          {typeof onViewMembership === "function" ? (
            <button type="button" className="msc-membership-complete-cta" onClick={onViewMembership}>
              Ver mi membresía
            </button>
          ) : null}
        </div>
      ) : null}

      {threeDs && !active && !approvedPendingActivation ? (
        <div className="msc-production-three-ds">
          <div className="msc-production-three-ds-head">
            <ShieldCheck size={20} />
            <div>
              <strong>Verificación de tu banco</strong>
              <span>Completa la autenticación para confirmar el pago.</span>
            </div>
          </div>
          {hiddenThreeDs ? (
            <WompiThreeDsFrame
              title={`Wompi Production 3DS ${threeDsStep}`}
              className="msc-3ds-hidden"
              html={threeDsHtml}
              hiddenExecution
            />
          ) : null}
          {visibleChallenge ? (
            <WompiThreeDsFrame
              title="Wompi Production 3DS Challenge"
              className="msc-3ds-challenge"
              html={threeDsHtml}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
